import { AppMessage } from '@/common/components';
import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { PageComponentsFormFields, PageComponentsFormInputs, PageComponentsQueryKeys, useCreateComponentsPageMutation } from '@/modules/Pages';

import { PageComponentsFormView } from './PageComponentsFormView';
import { pageComponentsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
}

export const PageComponentsCreateFormContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageComponentsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageComponentsFormInputs>>(null);

  const { mutate: createComponentMutate, isLoading: isCreateComponentLoading } = useCreateComponentsPageMutation(siteId)({
    onSuccess: async ({ id }) => {
      appMessageApi.success('Компонент успішно створено!');

      await queryClient.refetchQueries([PageComponentsQueryKeys.GetAll, siteId], { exact: true });

      navigate(AppRoutes.PageComponentsById.replace(':siteId', siteId).replace(':componentId', id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageComponentsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: PageComponentsFormFields = {
    name: undefined,
    content: undefined
  };

  const handleFinish = useCallback((values: PageComponentsFormFields): void => {
    setFormErrors(null);

    createComponentMutate(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.SitesTemplates.replace(':siteId', siteId));
  }, [siteId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <PageComponentsFormView
      form={ form }
      initialValues={ initialValues }
      formErrors={ formErrors }
      formName="page-components-create-form"
      formType="create"
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
      isFormLoading={ isCreateComponentLoading }
      validationSchema={ pageComponentsFormValidationSchema }
    />
  );
});
